import React from "react";
import { filterClass } from "../../utils/class-utils";
import { isEasyblocksEditorMode } from "../utils/accordion-shell";
import { getSectionBlockAlignClass, getTextAlignClass, resolveHorizontalContentAlign } from "../utils/content-alignment";
import type { CardProps } from "../types";

const IMAGE_BLOCK_DEFAULT_PLACEHOLDER_SRC = "https://placehold.co/400x400";

function isEnabled(value: CardProps["matchHeight"]) {
  return value === true || value === "true";
}

function getComponentTypeName(type: unknown) {
  const typed = type as { name?: string; displayName?: string; render?: { name?: string; displayName?: string } } | undefined;
  return typed?.displayName || typed?.name || typed?.render?.displayName || typed?.render?.name || "";
}

function isPlaceholderSlot(slot: CardProps["Image"] | CardProps["Headline"] | CardProps["RichText"] | CardProps["Button"]) {
  if (!slot) return false;

  const componentName = getComponentTypeName(slot.type);
  if (componentName === "Placeholder" || componentName === "TypePlaceholder") {
    return true;
  }

  const templateId = slot.props?.compiled?._component;
  return typeof templateId === "string" && templateId.startsWith("$Placeholder");
}

function isEmptyRenderedSlot(slot?: CardProps["Image"]) {
  return Boolean(slot) && slot.type === React.Fragment && !slot.props?.children;
}

function hasConfiguredCardImage(slot?: CardProps["Image"]) {
  return Boolean(slot) && !isPlaceholderSlot(slot) && !isEmptyRenderedSlot(slot);
}

function getConfiguredCardImageSrc(slot?: CardProps["Image"]) {
  if (!hasConfiguredCardImage(slot)) return "";

  const directSrc = typeof slot.props?.src === "string" ? slot.props.src.trim() : "";
  if (directSrc) return directSrc;

  const compiledSrc = typeof slot.props?.compiled?.props?.src === "string" ? slot.props.compiled.props.src.trim() : "";
  return compiledSrc;
}

function hasRenderableCardImage(slot?: CardProps["Image"]) {
  const src = getConfiguredCardImageSrc(slot);
  return Boolean(src) && src !== IMAGE_BLOCK_DEFAULT_PLACEHOLDER_SRC;
}

export function Card(props: CardProps) {
  const {
    Image,
    Headline,
    RichText,
    Button,
    cardBgClass,
    cardBorderClass,
    cardBorderColorClass,
    contentAlignClass,
    buttonColorClass,
    inheritedCardBgClass,
    inheritedCardBorderClass,
    inheritedCardBorderColorClass,
    inheritedContentAlignClass,
    inheritedButtonColorClass,
    inheritedContentAlign,
    matchHeight = false,
  } = props;

  const shouldMatchHeight = isEnabled(matchHeight);
  const isInEditor = isEasyblocksEditorMode();
  const resolvedCardBgClass = filterClass(inheritedCardBgClass) || filterClass(cardBgClass) || "text-bg-white";
  const resolvedCardBorderClass = filterClass(inheritedCardBorderClass) || filterClass(cardBorderClass);
  const resolvedCardBorderColorClass = filterClass(inheritedCardBorderColorClass) || filterClass(cardBorderColorClass);
  const resolvedButtonColorClass = inheritedButtonColorClass || buttonColorClass || "primary";
  const resolvedContentAlign = resolveHorizontalContentAlign(
    filterClass(contentAlignClass) || filterClass(inheritedContentAlignClass),
    inheritedContentAlign,
    "center"
  );
  const textAlignClass = getTextAlignClass(resolvedContentAlign);
  const bodyAlignClass = getSectionBlockAlignClass(textAlignClass);
  const hasCardBorder = resolvedCardBorderClass || resolvedCardBorderColorClass;
  const hasImage = Boolean(Image);
  const hasConfiguredImage = hasConfiguredCardImage(Image);
  const configuredImageSrc = getConfiguredCardImageSrc(Image);
  const hasHeadline = Boolean(Headline);
  const hasRichText = Boolean(RichText);
  const hasButton = Boolean(Button);
  const hasImageContent = hasRenderableCardImage(Image);
  const hasHeadlineContent = hasHeadline && !isPlaceholderSlot(Headline);
  const hasRichTextContent = hasRichText && !isPlaceholderSlot(RichText);
  const hasButtonContent = hasButton && !isPlaceholderSlot(Button);
  const isPlaceholderOnlyBody = isInEditor && !hasHeadlineContent && !hasRichTextContent && !hasButtonContent;
  const hasVisibleTopImage = isInEditor ? hasConfiguredImage : hasConfiguredImage && Boolean(configuredImageSrc);
  const isPlaceholderOnlyCard = isPlaceholderOnlyBody && !hasVisibleTopImage;
  const hasBodyContent = hasHeadline || hasRichText || hasButton;
  const shouldRenderTopImage = hasVisibleTopImage;
  const shouldRenderBody = hasBodyContent || (isInEditor && hasImage && !hasConfiguredImage);

  const classes = {
    container: [shouldMatchHeight ? "h-100" : ""]
      .filter(Boolean)
      .join(" "),
    card: [
      "card",
      "card-component",
      "rounded-4",
      "overflow-hidden",
      shouldMatchHeight ? "h-100" : "",
      resolvedCardBgClass,
      hasCardBorder ? "border" : "",
      resolvedCardBorderClass,
      resolvedCardBorderColorClass,
    ]
      .filter(Boolean)
      .join(" "),
    imageWrap: isInEditor ? "eb-card__image-wrap eb-card__image-wrap--editor overflow-hidden" : "ratio ratio-16x9",
    imageInner: "eb-card__image-content w-100 h-100",
    image: "w-100 h-100 object-fit-cover",
    body: [
      "eb-card__body",
      "card-body",
      "d-flex",
      "flex-column",
      isInEditor
        ? isPlaceholderOnlyCard
          ? "align-items-center justify-content-center text-center gap-3 h-100"
          : [bodyAlignClass, textAlignClass, "gap-3"].filter(Boolean).join(" ")
        : isPlaceholderOnlyCard
          ? "align-items-center justify-content-center text-center gap-3 h-100"
          : isPlaceholderOnlyBody
            ? "align-items-center text-center gap-3"
            : textAlignClass,
    ]
      .filter(Boolean)
      .join(" "),
    contentStack: ["d-flex", "flex-column", "gap-4", !isInEditor ? "h-100" : ""].filter(Boolean).join(" "),
    bodyItem: "w-100 component-item",
    buttonWrap: hasButton ? (isInEditor ? "" : "mt-auto") : "",
  };

  const renderTopImageSlot = () => {
    if (!shouldRenderTopImage || !Image) return null;

    const ImageComponent = Image.type as React.ComponentType<any>;

    if (isInEditor) {
      return (
        <div className={classes.imageWrap}>
          <div className={classes.imageInner}>
            <ImageComponent
              {...Image.props}
              paddingClass="py-0"
              horizontalAlignClass="no-class"
              border={false}
              borderWidth={undefined}
              borderColorClass="no-class"
              borderRadius="none"
              disableCardImageStyling={true}
              className={[Image.props?.className, classes.image].filter(Boolean).join(" ")}
            />
          </div>
        </div>
      );
    }

    return (
      <div className={classes.imageWrap}>
        <ImageComponent
          {...Image.props}
          paddingClass="py-0"
          horizontalAlignClass="no-class"
          border={false}
          borderWidth={undefined}
          borderColorClass="no-class"
          borderRadius="none"
          disableCardImageStyling={true}
          className={[Image.props?.className, classes.image].filter(Boolean).join(" ")}
        />
      </div>
    );
  };

  const renderImageInBody = () => {
    if (!Image || shouldRenderTopImage || (!isInEditor && !hasImageContent)) return null;

    const ImageComponent = Image.type as React.ComponentType<any>;
    return <ImageComponent {...Image.props} />;
  };

  const renderHeadline = () => {
    if (!Headline) return null;

    const HeadlineComponent = Headline.type as React.ComponentType<any>;

    return (
      <HeadlineComponent
        {...Headline.props}
        inheritedContentAlign={resolvedContentAlign}
        className={[Headline.props?.className, "mb-1"].filter(Boolean).join(" ")}
      />
    );
  };

  const renderRichText = () => {
    if (!RichText) return null;

    const RichTextComponent = RichText.type as React.ComponentType<any>;

    return (
      <RichTextComponent
        {...RichText.props}
        inheritedContentAlign={resolvedContentAlign}
        className={[RichText.props?.className, "card-text", "mb-0", textAlignClass].filter(Boolean).join(" ")}
      />
    );
  };

  const renderButton = () => {
    if (!Button) return null;

    const ButtonComponent = Button.type as React.ComponentType<any>;

    return (
      <ButtonComponent
        {...Button.props}
        variant={resolvedButtonColorClass}
        inheritedContentAlign={resolvedContentAlign}
      />
    );
  };

  return (
    <div className={classes.container}>
      <div className={classes.card}>
        {renderTopImageSlot()}
        {shouldRenderBody ? (
          <div className={classes.body}>
            {isInEditor ? (
              <>
                {Image && !shouldRenderTopImage ? <div className={classes.bodyItem}>{renderImageInBody()}</div> : null}
                {hasHeadline ? <div className={classes.bodyItem}>{renderHeadline()}</div> : null}
                {hasRichText ? <div className={classes.bodyItem}>{renderRichText()}</div> : null}
                {hasButton ? <div className={[classes.bodyItem, classes.buttonWrap].filter(Boolean).join(" ")}>{renderButton()}</div> : null}
              </>
            ) : (
              <>
                {renderImageInBody()}
                {hasHeadline || hasRichText || hasButton ? (
                  <div className={classes.contentStack}>
                    {hasHeadline ? renderHeadline() : null}
                    {hasRichText ? renderRichText() : null}
                    {hasButton ? <div className={classes.buttonWrap}>{renderButton()}</div> : null}
                  </div>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default Card;